import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Users, TrendingUp, Award, AlertTriangle, Target, Calendar, Send, Loader2 } from 'lucide-react';
import { useToast } from '../hooks/use-toast';
import { useAuth } from '../context/AuthContext';

const Dashboard = () => {
  const { user } = useAuth();
  const isManager = user?.role === 'manager';
  const [selectedTab, setSelectedTab] = useState('overview');
  const [selectedAgent, setSelectedAgent] = useState(null);
  const [rcaData, setRcaData] = useState(null);
  const [loadingRCA, setLoadingRCA] = useState(false);
  const [sendingTraining, setSendingTraining] = useState(false);
  const { toast } = useToast();
  
  // Real data from backend (replacing mock data)
  const [agents, setAgents] = useState([]);
  const [qaSessions, setQaSessions] = useState([]);
  const [trainingSessions, setTrainingSessions] = useState([]);
  const [trainingAssignments, setTrainingAssignments] = useState([]);
  const [stats, setStats] = useState(null);
  const [loadingData, setLoadingData] = useState(true);

  // Fetch real data from backend on mount
  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoadingData(true);
        
        // Fetch agents
        const agentsRes = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/agents`, {
          credentials: 'include'
        });
        if (agentsRes.ok) {
          const agentsData = await agentsRes.json();
          setAgents(agentsData);
        }
        
        // Fetch QA sessions
        const qaRes = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/qa-sessions`, {
          credentials: 'include'
        });
        if (qaRes.ok) {
          const qaData = await qaRes.json();
          setQaSessions(qaData);
        }
        
        // Fetch training sessions
        const trainRes = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/training-sessions`, {
          credentials: 'include'
        });
        if (trainRes.ok) {
          const trainData = await trainRes.json();
          setTrainingSessions(trainData);
        }
        
        // Fetch stats
        const statsRes = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/stats`, {
          credentials: 'include'
        });
        if (statsRes.ok) {
          const statsData = await statsRes.json();
          setStats(statsData);
        }
        
        // Fetch training assignments
        const assignmentsRes = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/training-assignments`, {
          credentials: 'include'
        });
        if (assignmentsRes.ok) {
          const assignmentsData = await assignmentsRes.json();
          setTrainingAssignments(assignmentsData);
        }
        
      } catch (error) {
        console.error('Error fetching data:', error);
        toast({
          title: 'Error',
          description: 'Failed to load dashboard data',
          variant: 'destructive'
        });
      } finally {
        setLoadingData(false);
      }
    };
    
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const getInitials = (name) => {
    return name.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2);
  };

  const getAgentAvg = (agentId) => {
    const sessions = [...qaSessions, ...trainingSessions].filter(s => s.agent_id === agentId);
    if (sessions.length === 0) return null;
    const avg = sessions.reduce((sum, s) => sum + s.score, 0) / sessions.length;
    return Math.round(avg);
  };

  const getScoreClass = (score) => {
    if (score >= 80) return 'text-[#34c78a] bg-[#34c78a]/10 border-[#34c78a]/30';
    if (score >= 60) return 'text-[#f5a623] bg-[#f5a623]/10 border-[#f5a623]/30';
    return 'text-[#f05252] bg-[#f05252]/10 border-[#f05252]/30';
  };

  const colors = ['#4f8ef7', '#34c78a', '#f5a623', '#9b6dff', '#f05252'];

  // Fetch RCA data for selected agent
  const fetchAgentRCA = async (agentId) => {
    setLoadingRCA(true);
    try {
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/rca/agent/${agentId}`,
        { credentials: 'include' }
      );
      
      if (response.ok) {
        const data = await response.json();
        setRcaData(data);
      } else {
        throw new Error('Failed to fetch RCA data');
      }
    } catch (error) {
      toast({
        title: 'Error',
        description: 'Failed to analyze agent performance',
        variant: 'destructive'
      });
    } finally {
      setLoadingRCA(false);
    }
  };

  // Send training to agent
  const sendTrainingToAgent = async (agentId) => {
    setSendingTraining(true);
    try {
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/rca/send-training/${agentId}`,
        { 
          method: 'POST',
          credentials: 'include' 
        }
      );
      
      const data = await response.json();
      
      if (response.ok && data.success) {
        toast({
          title: 'Training Sent!',
          description: `${data.training_plan.total_modules} personalized training modules assigned`,
        });
        setSelectedAgent(null);
        setRcaData(null);
      } else {
        toast({
          title: 'Info',
          description: data.message || 'Agent is performing well',
        });
      }
    } catch (error) {
      toast({
        title: 'Error',
        description: 'Failed to send training',
        variant: 'destructive'
      });
    } finally {
      setSendingTraining(false);
    }
  };

  // Open agent detail modal - use agent_id from backend
  const openAgentDetail = (agent) => {
    setSelectedAgent(agent);
    fetchAgentRCA(agent.agent_id);  // Use agent_id from backend
  };

  return (
    <div className="min-h-screen bg-[#0d0f12] ml-56">
      {/* Agent RCA Detail Modal */}
      <Dialog open={!!selectedAgent} onOpenChange={() => { setSelectedAgent(null); setRcaData(null); }}>
        <DialogContent className="bg-[#1a1b23] border-[#2a2d3a] max-w-2xl">
          <DialogHeader>
            <DialogTitle className="text-white text-xl">
              {selectedAgent?.name} - Performance Analysis
            </DialogTitle>
            <DialogDescription className="text-gray-400">
              Root Cause Analysis & Training Recommendations
            </DialogDescription>
          </DialogHeader>
          
          {loadingRCA ? (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="w-8 h-8 text-[#4f8ef7] animate-spin" />
              <span className="ml-3 text-gray-400">Analyzing performance data...</span>
            </div>
          ) : rcaData ? (
            <div className="space-y-4">
              {/* Overall Score */}
              <Card className="bg-[#161920] border-[#2a3040]">
                <CardContent className="pt-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <div className="text-sm text-gray-400">Overall Score</div>
                      <div className={`text-3xl font-bold ${
                        rcaData.overall_score >= 80 ? 'text-[#34c78a]' : 
                        rcaData.overall_score >= 60 ? 'text-[#f5a623]' : 
                        'text-[#f05252]'
                      }`}>
                        {rcaData.overall_score}
                      </div>
                    </div>
                    <div className="text-right">
                      <div className="text-sm text-gray-400">Total Sessions</div>
                      <div className="text-2xl font-semibold text-white">
                        {rcaData.total_sessions}
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Weaknesses */}
              {rcaData.weaknesses && rcaData.weaknesses.length > 0 ? (
                <>
                  <div>
                    <h3 className="text-sm font-medium text-white mb-3">⚠️ Areas Needing Improvement</h3>
                    <div className="space-y-2">
                      {rcaData.weaknesses.map((weakness) => (
                        <Card key={weakness.parameter} className={`bg-[#161920] border-l-2 ${
                          weakness.severity === 'high' ? 'border-l-[#f05252]' :
                          weakness.severity === 'medium' ? 'border-l-[#f5a623]' :
                          'border-l-[#555f72]'
                        }`}>
                          <CardContent className="pt-4 pb-4">
                            <div className="flex items-center justify-between mb-2">
                              <span className="font-medium text-white">{weakness.parameter}</span>
                              <div className="flex items-center gap-2">
                                <Badge className={`${
                                  weakness.severity === 'high' ? 'bg-[#f05252]/10 text-[#f05252] border-[#f05252]/30' :
                                  weakness.severity === 'medium' ? 'bg-[#f5a623]/10 text-[#f5a623] border-[#f5a623]/30' :
                                  'bg-[#555f72]/10 text-[#555f72] border-[#555f72]/30'
                                } border text-xs`}>
                                  {weakness.severity}
                                </Badge>
                                <span className="text-sm font-mono text-gray-400">
                                  {weakness.avg_score}/{weakness.max_score}
                                </span>
                              </div>
                            </div>
                            <div className="text-xs text-gray-500">
                              {weakness.sessions_count} sessions · {weakness.percentage}% · {weakness.trend}
                            </div>
                          </CardContent>
                        </Card>
                      ))}
                    </div>
                  </div>

                  {/* Training Recommendations */}
                  {rcaData.recommended_training && (
                    <Card className="bg-[#4f8ef7]/5 border-[#4f8ef7]/30">
                      <CardContent className="pt-5 pb-5">
                        <div className="text-xs font-mono tracking-wider text-[#4f8ef7] uppercase mb-3">
                          📚 Recommended Training
                        </div>
                        <div className="space-y-2">
                          <div className="text-sm text-gray-300">
                            <strong>Focus Areas:</strong> {rcaData.recommended_training.focus_areas.join(', ')}
                          </div>
                          <div className="text-sm text-gray-300">
                            <strong>Suggested Scenarios:</strong>
                            <div className="mt-1 flex flex-wrap gap-1">
                              {rcaData.recommended_training.suggested_scenarios.map((scenario) => (
                                <Badge key={scenario} variant="outline" className="border-[#4f8ef7]/30 text-[#4f8ef7] text-xs">
                                  {scenario}
                                </Badge>
                              ))}
                            </div>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  )}

                  {/* Send Training Button */}
                  <Button
                    onClick={() => sendTrainingToAgent(selectedAgent.agent_id)}
                    disabled={sendingTraining}
                    className="w-full bg-[#34c78a] hover:bg-[#34c78a]/90 text-white"
                  >
                    {sendingTraining ? (
                      <>
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                        Sending Training...
                      </>
                    ) : (
                      <>
                        <Send className="w-4 h-4 mr-2" />
                        Send Personalized Training
                      </>
                    )}
                  </Button>
                </>
              ) : (
                <Card className="bg-[#34c78a]/5 border-[#34c78a]/30">
                  <CardContent className="pt-6 pb-6 text-center">
                    <div className="text-4xl mb-3">🎉</div>
                    <div className="text-white font-medium mb-1">Great Performance!</div>
                    <div className="text-sm text-gray-400">
                      This agent is meeting all performance standards.
                    </div>
                  </CardContent>
                </Card>
              )}
            </div>
          ) : null}
        </DialogContent>
      </Dialog>

      <div className="max-w-7xl mx-auto p-6">
        {/* Loading State */}
        {loadingData ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="w-8 h-8 text-[#4f8ef7] animate-spin mr-3" />
            <span className="text-gray-400">Loading dashboard data...</span>
          </div>
        ) : (
          <>
        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h1 className="text-3xl font-semibold text-white mb-2">
                {isManager ? 'Team Dashboard' : 'My Performance Dashboard'}
              </h1>
              <p className="text-gray-400">
                {isManager 
                  ? (stats ? `${stats.agent_count} agents · ${stats.total_sessions} sessions · Team avg: ${stats.avg_score}` : 'Loading...') 
                  : 'Track your progress and training assignments'}
              </p>
            </div>
          </div>
        </div>

        {/* Tabs */}
        <div className="flex gap-1 border-b border-[#2a3040] mb-6">
          {[
            { id: 'overview', label: '📊 Overview' },
            ...(isManager ? [{ id: 'agents', label: '👥 All Agents' }] : []),
          ].map(tab => (
            <button
              key={tab.id}
              onClick={() => setSelectedTab(tab.id)}
              className={`px-5 py-3 text-sm font-medium transition-all border-b-2 -mb-[1px] ${
                selectedTab === tab.id
                  ? 'text-[#4f8ef7] border-[#4f8ef7]'
                  : 'text-gray-400 border-transparent hover:text-white'
              }`}
            >
              {tab.label}
            </button>
          ))}
        </div>

        {selectedTab === 'overview' && (
          <div>
            {/* KPI Cards */}
            {isManager ? (
              // Manager View - Team Stats
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
              <Card className="bg-[#161920] border-[#2a3040]">
                <CardContent className="pt-6">
                  <div className="flex items-center gap-3 mb-3">
                    <div className="w-10 h-10 rounded-lg bg-[#4f8ef7]/10 border border-[#4f8ef7]/30 flex items-center justify-center">
                      <Users className="w-5 h-5 text-[#4f8ef7]" />
                    </div>
                    <div className="text-xs font-mono tracking-wider text-gray-500 uppercase">
                      Agents
                    </div>
                  </div>
                  <div className="text-3xl font-semibold text-[#4f8ef7] mb-1">
                    {stats?.agent_count || 0}
                  </div>
                  <div className="text-xs text-gray-500">on the team</div>
                </CardContent>
              </Card>

              <Card className="bg-[#161920] border-[#2a3040]">
                <CardContent className="pt-6">
                  <div className="flex items-center gap-3 mb-3">
                    <div className="w-10 h-10 rounded-lg bg-[#34c78a]/10 border border-[#34c78a]/30 flex items-center justify-center">
                      <TrendingUp className="w-5 h-5 text-[#34c78a]" />
                    </div>
                    <div className="text-xs font-mono tracking-wider text-gray-500 uppercase">
                      Avg Score
                    </div>
                  </div>
                  <div className="text-3xl font-semibold text-[#34c78a] mb-1">
                    {stats?.avg_score}
                  </div>
                  <div className="text-xs text-gray-500">all sessions</div>
                </CardContent>
              </Card>

              <Card className="bg-[#161920] border-[#2a3040]">
                <CardContent className="pt-6">
                  <div className="flex items-center gap-3 mb-3">
                    <div className="w-10 h-10 rounded-lg bg-[#9b6dff]/10 border border-[#9b6dff]/30 flex items-center justify-center">
                      <Target className="w-5 h-5 text-[#9b6dff]" />
                    </div>
                    <div className="text-xs font-mono tracking-wider text-gray-500 uppercase">
                      Sessions
                    </div>
                  </div>
                  <div className="text-3xl font-semibold text-[#9b6dff] mb-1">
                    {stats?.total_sessions}
                  </div>
                  <div className="text-xs text-gray-500">logged total</div>
                </CardContent>
              </Card>

              <Card className="bg-[#161920] border-[#2a3040]">
                <CardContent className="pt-6">
                  <div className="flex items-center gap-3 mb-3">
                    <div className="w-10 h-10 rounded-lg bg-[#f5a623]/10 border border-[#f5a623]/30 flex items-center justify-center">
                      <AlertTriangle className="w-5 h-5 text-[#f5a623]" />
                    </div>
                    <div className="text-xs font-mono tracking-wider text-gray-500 uppercase">
                      Need Coaching
                    </div>
                  </div>
                  <div className="text-3xl font-semibold text-[#f5a623] mb-1">
                    {stats?.need_coaching}
                  </div>
                  <div className="text-xs text-gray-500">below 70 avg</div>
                </CardContent>
              </Card>
            </div>
            ) : (
              // Agent View - Personal Stats
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
                <Card className="bg-[#161920] border-[#2a3040]">
                  <CardContent className="pt-6">
                    <div className="flex items-center gap-3 mb-3">
                      <div className="w-10 h-10 rounded-lg bg-[#34c78a]/10 border border-[#34c78a]/30 flex items-center justify-center">
                        <TrendingUp className="w-5 h-5 text-[#34c78a]" />
                      </div>
                      <div className="text-xs font-mono tracking-wider text-gray-500 uppercase">
                        My Avg Score
                      </div>
                    </div>
                    <div className="text-3xl font-semibold text-[#34c78a] mb-1">
                      {stats?.avg_score || 'N/A'}
                    </div>
                    <div className="text-xs text-gray-500">all my sessions</div>
                  </CardContent>
                </Card>

                <Card className="bg-[#161920] border-[#2a3040]">
                  <CardContent className="pt-6">
                    <div className="flex items-center gap-3 mb-3">
                      <div className="w-10 h-10 rounded-lg bg-[#9b6dff]/10 border border-[#9b6dff]/30 flex items-center justify-center">
                        <Target className="w-5 h-5 text-[#9b6dff]" />
                      </div>
                      <div className="text-xs font-mono tracking-wider text-gray-500 uppercase">
                        My Sessions
                      </div>
                    </div>
                    <div className="text-3xl font-semibold text-[#9b6dff] mb-1">
                      {stats?.total_sessions || 0}
                    </div>
                    <div className="text-xs text-gray-500">completed</div>
                  </CardContent>
                </Card>

                <Card className="bg-[#161920] border-[#2a3040]">
                  <CardContent className="pt-6">
                    <div className="flex items-center gap-3 mb-3">
                      <div className="w-10 h-10 rounded-lg bg-[#4f8ef7]/10 border border-[#4f8ef7]/30 flex items-center justify-center">
                        <Award className="w-5 h-5 text-[#4f8ef7]" />
                      </div>
                      <div className="text-xs font-mono tracking-wider text-gray-500 uppercase">
                        Rank
                      </div>
                    </div>
                    <div className="text-3xl font-semibold text-[#4f8ef7] mb-1">
                      {stats?.rank || '-'}
                    </div>
                    <div className="text-xs text-gray-500">in team</div>
                  </CardContent>
                </Card>
              </div>
            )}

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
              {/* Training Assignments (if user has any) */}
              {trainingAssignments.length > 0 && (
                <Card className="bg-[#161920] border-[#2a3040]">
                  <CardHeader className="border-b border-[#2a3040]">
                    <CardTitle className="text-white text-base flex items-center gap-2">
                      🎯 My Assigned Training
                      <Badge className="bg-[#34c78a]/10 text-[#34c78a] border-[#34c78a]/30">
                        {trainingAssignments.filter(a => a.status === 'pending').length} pending
                      </Badge>
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="pt-6">
                    <div className="space-y-3">
                      {trainingAssignments.slice(0, 3).map((assignment) => (
                        <div key={assignment.assignment_id} className="p-3 rounded-lg bg-[#1e2229] border border-[#2a3040]">
                          <div className="flex items-start justify-between mb-2">
                            <div className="flex-1">
                              <div className="text-sm font-medium text-white mb-1">
                                {assignment.focus_areas.join(', ')}
                              </div>
                              <div className="text-xs text-gray-400">
                                Assigned by {assignment.assigned_by_name}
                              </div>
                            </div>
                            <Badge className={`${
                              assignment.priority === 'high' ? 'bg-red-500/10 text-red-500 border-red-500/30' :
                              assignment.priority === 'medium' ? 'bg-amber-500/10 text-amber-500 border-amber-500/30' :
                              'bg-blue-500/10 text-blue-500 border-blue-500/30'
                            } border text-xs`}>
                              {assignment.priority}
                            </Badge>
                          </div>
                          <div className="flex gap-2 mt-2">
                            <Button
                              size="sm"
                              onClick={() => window.location.href = '/training'}
                              className="bg-[#34c78a] hover:bg-[#34c78a]/90 text-white h-7 text-xs"
                            >
                              Start Training
                            </Button>
                            <Badge variant="outline" className="border-[#2a3040] text-gray-400 text-xs">
                              {assignment.scenarios.length} scenarios
                            </Badge>
                          </div>
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              )}
              
              {/* Leaderboard */}
              <Card className="bg-[#161920] border-[#2a3040]">
                <CardHeader className="border-b border-[#2a3040]">
                  <CardTitle className="text-white text-base">🏆 Leaderboard</CardTitle>
                </CardHeader>
                <CardContent className="pt-6">
                  <div className="space-y-3">
                    {agents
                      .map((agent, idx) => ({ ...agent, avg: getAgentAvg(agent.agent_id), idx }))
                      .filter(a => a.avg !== null)
                      .sort((a, b) => b.avg - a.avg)
                      .slice(0, 5)
                      .map((agent) => {
                        const medals = ['🥇', '🥈', '🥉'];
                        const position = agents
                          .map((a, i) => ({ ...a, avg: getAgentAvg(a.agent_id), idx: i }))
                          .filter(a => a.avg !== null)
                          .sort((a, b) => b.avg - a.avg)
                          .findIndex(a => a.agent_id === agent.agent_id);
                        
                        return (
                          <div key={agent.agent_id} className="flex items-center gap-3">
                            <span className="text-lg w-6 text-center">
                              {medals[position] || position + 1}
                            </span>
                            <div 
                              className="w-8 h-8 rounded-full flex items-center justify-center text-xs font-semibold text-white"
                              style={{ background: colors[agent.idx % colors.length] }}
                            >
                              {getInitials(agent.name)}
                            </div>
                            <div className="flex-1">
                              <div className="text-sm font-medium text-white">{agent.name}</div>
                              <div className="text-xs text-gray-500">
                                {qaSessions.filter(s => s.agent_id === agent.agent_id).length + 
                                 trainingSessions.filter(s => s.agent_id === agent.agent_id).length} sessions
                              </div>
                            </div>
                            <span className={`text-base font-semibold font-mono ${
                              agent.avg >= 80 ? 'text-[#34c78a]' : 
                              agent.avg >= 60 ? 'text-[#f5a623]' : 
                              'text-[#f05252]'
                            }`}>
                              {agent.avg}
                            </span>
                          </div>
                        );
                      })}
                  </div>
                </CardContent>
              </Card>

              {/* Recent Sessions */}
              <Card className="bg-[#161920] border-[#2a3040]">
                <CardHeader className="border-b border-[#2a3040]">
                  <CardTitle className="text-white text-base">Recent Sessions</CardTitle>
                </CardHeader>
                <CardContent className="pt-6">
                  <div className="space-y-3">
                    {[...qaSessions, ...trainingSessions]
                      .sort((a, b) => new Date(b.created_at) - new Date(a.created_at))
                      .slice(0, 5)
                      .map((session) => {
                        const agent = agents.find(a => a.agent_id === session.agent_id);
                        const isQA = qaSessions.includes(session);
                        const sessionKey = session.session_id || `${session.agent_id}-${session.created_at}`;
                        return (
                          <div key={sessionKey} className="flex items-center gap-3 pb-3 border-b border-[#2a3040] last:border-0">
                            <Badge className={`${
                              isQA 
                                ? 'bg-[#9b6dff]/10 text-[#9b6dff] border-[#9b6dff]/30' 
                                : 'bg-[#4f8ef7]/10 text-[#4f8ef7] border-[#4f8ef7]/30'
                            } border text-xs font-mono`}>
                              {isQA ? 'QA' : 'Train'}
                            </Badge>
                            <div className="flex-1 min-w-0">
                              <div className="text-sm font-medium text-white truncate">
                                {agent?.name || 'Unknown'}
                              </div>
                              <div className="text-xs text-gray-500 truncate">
                                {session.scenario || 'QA Session'}
                              </div>
                            </div>
                            <Badge className={`border ${getScoreClass(session.score)}`}>
                              {session.score}
                            </Badge>
                          </div>
                        );
                      })}
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* Team Performance Note */}
            <Card className="bg-[#4f8ef7]/5 border-[#4f8ef7]/30">
              <CardContent className="pt-5 pb-5">
                <div className="flex items-start gap-3">
                  <TrendingUp className="w-5 h-5 text-[#4f8ef7] flex-shrink-0 mt-0.5" />
                  <div>
                    <h3 className="text-sm font-medium text-white mb-1">
                      Team Performance Summary
                    </h3>
                    <p className="text-sm text-gray-400 leading-relaxed">
                      The team is performing well with an average score of {stats?.avg_score}. 
                      {stats?.need_coaching > 0 && (
                        <> {stats?.need_coaching} agent(s) need additional coaching to improve performance.</>
                      )}
                      {stats?.need_coaching === 0 && (
                        <> All agents are meeting performance standards. Keep up the excellent work!</>
                      )}
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        )}

        {selectedTab === 'agents' && (
          <Card className="bg-[#161920] border-[#2a3040]">
            <CardHeader className="border-b border-[#2a3040]">
              <CardTitle className="text-white text-base flex items-center justify-between">
                <span>All Agents</span>
                <span className="text-xs font-mono text-gray-500">
                  {agents.length} agents
                </span>
              </CardTitle>
            </CardHeader>
            <CardContent className="pt-6">
              <div className="space-y-4">
                {agents.map((agent, idx) => {
                  const avg = getAgentAvg(agent.agent_id);
                  const qaSess = qaSessions.filter(s => s.agent_id === agent.agent_id).length;
                  const trainSess = trainingSessions.filter(s => s.agent_id === agent.agent_id).length;
                  return (
                    <div 
                      key={agent.agent_id} 
                      className="flex items-center gap-4 p-4 rounded-lg bg-[#1e2229] border border-[#2a3040] hover:border-[#4f8ef7]/50 transition-all"
                    >
                      <div 
                        className="w-12 h-12 rounded-full flex items-center justify-center text-sm font-semibold text-white flex-shrink-0"
                        style={{ background: colors[idx % colors.length] }}
                      >
                        {getInitials(agent.name)}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="text-base font-medium text-white mb-1">
                          {agent.name}
                        </div>
                        <div className="flex gap-2 flex-wrap">
                          {agent.agent_code && (
                            <span className="text-xs text-gray-500 font-mono">
                              {agent.agent_code}
                            </span>
                          )}
                          {agent.team && (
                            <span className="text-xs text-gray-500">· {agent.team}</span>
                          )}
                          {agent.role && (
                            <span className="text-xs text-gray-500">· {agent.role}</span>
                          )}
                        </div>
                      </div>
                      <div className="flex items-center gap-4">
                        {avg !== null ? (
                          <Badge className={`border ${getScoreClass(avg)} text-base font-mono px-3 py-1`}>
                            {avg}
                          </Badge>
                        ) : (
                          <Badge variant="outline" className="border-[#2a3040] text-gray-500">
                            No data
                          </Badge>
                        )}
                        <div className="text-right">
                          <div className="text-sm font-mono text-gray-400">
                            {qaSess + trainSess}
                          </div>
                          <div className="text-[10px] text-gray-600">sessions</div>
                        </div>
                        <div className="flex gap-2 items-center">
                          {avg !== null && avg >= 80 ? (
                            <Badge className="bg-[#34c78a]/10 text-[#34c78a] border-[#34c78a]/30 border text-xs">
                              ✓ On track
                            </Badge>
                          ) : avg !== null && avg >= 60 ? (
                            <Badge className="bg-[#f5a623]/10 text-[#f5a623] border-[#f5a623]/30 border text-xs">
                              ⚠ Needs work
                            </Badge>
                          ) : avg !== null ? (
                            <Badge className="bg-[#f05252]/10 text-[#f05252] border-[#f05252]/30 border text-xs">
                              ⚡ Critical
                            </Badge>
                          ) : (
                            <Badge variant="outline" className="border-[#2a3040] text-gray-500 text-xs">
                              No data
                            </Badge>
                          )}
                          {avg !== null && avg < 80 && (
                            <Button
                              size="sm"
                              onClick={() => openAgentDetail(agent)}
                              className="bg-[#4f8ef7] hover:bg-[#4f8ef7]/90 text-white h-8"
                            >
                              <Target className="w-3 h-3 mr-1" />
                              Analyze
                            </Button>
                          )}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </CardContent>
          </Card>
        )}
          </>
        )}
      </div>
    </div>
  );
};

export default Dashboard;
